import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FilterPanel from './components/FilterPanel';
import PlayerCard from './components/PlayerCard';
import PlayerDetailModal from './components/PlayerDetailModal';
import AdminPlayerProfileModal from './components/AdminPlayerProfileModal';
import BulkEditModal from './components/BulkEditModal';
import ComparisonPanel from './components/ComparisonPanel';
import { playerProfileService } from '../../services/playerMasterService';
import { performanceStatsService } from '../../services/playerMasterService';
import { useAuth } from '../../contexts/AuthContext';
import { playerMediaStorage } from '../../services/storageService';
import { realtimeMonitoringService } from '../../services/realtimeMonitoringService';
import { Checkbox } from '../../components/ui/Checkbox';
import Input from '../../components/ui/Input';

const PlayerProfiles = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasRole, hasAnyRole, user } = useAuth();
  const isAdmin = hasRole('admin');
  const canViewProfiles = hasAnyRole(['scout', 'club', 'player', 'coach', 'admin']);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [comparisonPlayers, setComparisonPlayers] = useState([]);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);
  const [sortBy, setSortBy] = useState('rating');
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [filters, setFilters] = useState({
    search: '',
    position: 'all',
    league: 'all',
    nationality: 'all',
    ageMin: '',
    ageMax: '',
    valueMin: '',
    valueMax: '',
    immediateTransfer: false,
    fifaVerified: false,
    hasVideo: false
  });

  const fetchPlayers = async () => {
    setLoading(true);
    const fetchStartTime = performance.now();
    
    // Track data fetch operation
    const fetchOperation = await realtimeMonitoringService?.trackDatabaseWrite({
      type: 'read',
      tableName: 'player_master_profiles',
      userId: user?.id,
      metadata: {
        operation: 'fetch_all_players',
        page: 'player-profiles',
        isAdmin: isAdmin
      }
    });
    
    try {
      // Use getAllForAdmin() for admin users to see all players including those without user_id
      const response = isAdmin 
        ? await playerProfileService?.getAllForAdmin()
        : await playerProfileService?.getAll();
      const playersData = response?.data || [];

      if (!playersData || playersData?.length === 0) {
        // Track when no data is returned
        await realtimeMonitoringService?.trackDisplayFailure({
          componentName: 'PlayerProfiles',
          type: 'no_data_returned',
          message: 'No players found in database',
          userId: user?.id,
          metadata: {
            responseData: response,
            expectedData: 'player profiles array',
            isAdmin: isAdmin
          }
        });
      }

      // Fetch ALL performance stats in a single query to avoid N+1 concurrent requests
      let allStatsMap = {};
      try {
        const allStatsResponse = await performanceStatsService?.getAllStats();
        const allStats = allStatsResponse?.data || [];
        // Group stats by playerId for O(1) lookup
        allStats?.forEach(stat => {
          if (!allStatsMap?.[stat?.playerId]) {
            allStatsMap[stat?.playerId] = [];
          }
          allStatsMap?.[stat?.playerId]?.push(stat);
        });
      } catch (statsError) {
        console.error('Error fetching all performance stats:', statsError);
      }

      // Load profile photos fully sequentially — one at a time — to prevent Supabase storage lock contention
      const playersWithPhotosAndStats = [];
      for (const player of playersData) {
        let playerWithPhoto = { ...player };

        // Load profile photo one at a time
        if (player?.profilePhoto) {
          try {
            const result = await playerMediaStorage?.getSignedUrl(player?.profilePhoto, 3600);
            if (result?.data?.signedUrl) {
              playerWithPhoto.profilePhotoUrl = result?.data?.signedUrl;
            }
          } catch (photoError) {
            console.error(`Error loading photo for player ${player?.id}:`, photoError);
          }
        }

        // Use pre-fetched stats from the single bulk query
        const performanceStats = allStatsMap?.[player?.id] || [];

        const careerTotals = performanceStats?.reduce((totals, stat) => ({
          totalAppearances: (totals?.totalAppearances || 0) + (stat?.appearances || 0),
          goalsScored: (totals?.goalsScored || 0) + (stat?.goalsScored || 0),
          assists: (totals?.assists || 0) + (stat?.assists || 0),
          minutesPlayed: (totals?.minutesPlayed || 0) + (stat?.minutesPlayed || 0)
        }), { totalAppearances: 0, goalsScored: 0, assists: 0, minutesPlayed: 0 });

        const statsWithRating = performanceStats?.filter(stat => stat?.motmAwards > 0);
        const averageRating = statsWithRating?.length > 0
          ? statsWithRating?.reduce((sum, stat) => sum + (stat?.motmAwards || 0), 0) / statsWithRating?.length
          : 7.5;

        playerWithPhoto = {
          ...playerWithPhoto,
          totalAppearances: careerTotals?.totalAppearances,
          goalsScored: careerTotals?.goalsScored,
          assists: careerTotals?.assists,
          minutesPlayed: careerTotals?.minutesPlayed,
          averageRating: averageRating
        };

        playersWithPhotosAndStats?.push(playerWithPhoto);
      }
      
      setPlayers(playersWithPhotosAndStats);
      
      // Track successful fetch
      const fetchEndTime = performance.now();
      await fetchOperation?.complete(true, null, null);
      
      // Log performance
      await realtimeMonitoringService?.trackDatabaseWrite({
        type: 'read',
        tableName: 'player_master_profiles',
        userId: user?.id,
        metadata: {
          operation: 'fetch_complete',
          playerCount: playersWithPhotosAndStats?.length,
          duration: fetchEndTime - fetchStartTime,
          photosLoaded: playersWithPhotosAndStats?.filter(p => p?.profilePhotoUrl)?.length
        }
      });
      
    } catch (error) {
      console.error('Error fetching players:', error);
      
      // Track fetch failure
      await fetchOperation?.complete(false, error);
      
      // Track display failure with detailed error
      await realtimeMonitoringService?.trackDisplayFailure({
        componentName: 'PlayerProfiles',
        type: 'data_load_failed',
        error,
        userId: user?.id,
        metadata: {
          operation: 'fetchPlayers',
          errorMessage: error?.message,
          errorStack: error?.stack
        }
      });
      
      setPlayers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlayers();
  }, []);

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 0;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today?.getFullYear() - birthDate?.getFullYear();
    const monthDiff = today?.getMonth() - birthDate?.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
      age--;
    }
    return age;
  };

  const filteredPlayers = useMemo(() => {
    return players?.filter((player) => {
      if (filters?.search && !player?.fullName?.toLowerCase()?.includes(filters?.search?.toLowerCase())) {
        return false;
      }
      if (filters?.position !== 'all' && player?.primaryPosition?.toLowerCase() !== filters?.position) {
        return false;
      }
      if (filters?.nationality !== 'all' && player?.nationalityPrimary?.toLowerCase() !== filters?.nationality) {
        return false;
      }
      let age = calculateAge(player?.dateOfBirth);
      if (filters?.ageMin && age < parseInt(filters?.ageMin)) {
        return false;
      }
      if (filters?.ageMax && age > parseInt(filters?.ageMax)) {
        return false;
      }
      if (filters?.valueMin && player?.currentMarketValue < parseFloat(filters?.valueMin)) {
        return false;
      }
      if (filters?.valueMax && player?.currentMarketValue > parseFloat(filters?.valueMax)) {
        return false;
      }
      if (filters?.immediateTransfer && !player?.availableForTransfer) {
        return false;
      }
      if (filters?.fifaVerified && !player?.fifaVerified) {
        return false;
      }
      return true;
    });
  }, [filters, players]);

  const sortedPlayers = useMemo(() => {
    const sorted = [...filteredPlayers];
    switch (sortBy) {
      case 'rating':
        return sorted?.sort((a, b) => (b?.rating || 0) - (a?.rating || 0));
      case 'value':
        return sorted?.sort((a, b) => (b?.currentMarketValue || 0) - (a?.currentMarketValue || 0));
      case 'age':
        return sorted?.sort((a, b) => calculateAge(a?.dateOfBirth) - calculateAge(b?.dateOfBirth));
      case 'goals':
        return sorted?.sort((a, b) => (b?.goals || 0) - (a?.goals || 0));
      default:
        return sorted;
    }
  }, [filteredPlayers, sortBy]);

  const handleClearFilters = () => {
    setFilters({
      search: '',
      position: 'all',
      league: 'all',
      nationality: 'all',
      ageMin: '',
      ageMax: '',
      valueMin: '',
      valueMax: '',
      immediateTransfer: false,
      fifaVerified: false,
      hasVideo: false
    });
  };

  const handleViewProfile = (player) => {
    setSelectedPlayer(player);
    setIsModalOpen(true);
  };

  const handlePlayerUpdated = (updatedPlayer) => {
    if (updatedPlayer && updatedPlayer?.id) {
      setSelectedPlayer(updatedPlayer);
    }
    fetchPlayers();
  };

  const handleCompare = (player) => {
    if (comparisonPlayers?.find((p) => p?.id === player?.id)) {
      setComparisonPlayers(comparisonPlayers?.filter((p) => p?.id !== player?.id));
    } else if (comparisonPlayers?.length < 4) {
      setComparisonPlayers([...comparisonPlayers, player]);
      setIsComparisonOpen(true);
    }
  };

  const handleRemoveFromComparison = (playerId) => {
    setComparisonPlayers(comparisonPlayers?.filter((p) => p?.id !== playerId));
    if (comparisonPlayers?.length === 1) {
      setIsComparisonOpen(false);
    }
  };

  const handleClearComparison = () => {
    setComparisonPlayers([]);
    setIsComparisonOpen(false);
  };

  const handleRequestConsultation = (player) => {
    navigate('/contact', { state: { playerName: player?.name } });
  };

  const handleViewDetails = async (player) => {
    try {
      setSelectedPlayer(player);
      setIsModalOpen(true);
      
      // Track player detail view
      await realtimeMonitoringService?.trackDatabaseWrite({
        type: 'read',
        tableName: 'player_master_profiles',
        recordId: player?.id,
        userId: user?.id,
        metadata: {
          operation: 'view_player_details',
          playerName: player?.fullName,
          playerId: player?.id
        }
      });
    } catch (error) {
      console.error('Error opening player details:', error);
      
      // Track modal open failure
      await realtimeMonitoringService?.trackDisplayFailure({
        componentName: 'PlayerProfiles',
        type: 'modal_open_failed',
        error,
        userId: user?.id,
        metadata: {
          playerId: player?.id,
          playerName: player?.fullName
        }
      });
    }
  };

  const handlePlayerClick = (player) => {
    if (bulkSelectMode) {
      togglePlayerSelection(player?.id);
    } else {
      setSelectedPlayer(player);
      setIsModalOpen(true);
    }
  };

  const togglePlayerSelection = (playerId) => {
    setSelectedPlayerIds(prev => {
      if (prev?.includes(playerId)) {
        return prev?.filter(id => id !== playerId);
      } else {
        return [...prev, playerId];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedPlayerIds?.length === sortedPlayers?.length) {
      setSelectedPlayerIds([]);
    } else {
      setSelectedPlayerIds(sortedPlayers?.map(p => p?.id));
    }
  };

  const handleBulkEditClick = () => {
    if (selectedPlayerIds?.length === 0) {
      alert('Please select at least one player to edit');
      return;
    }
    setShowBulkEditModal(true);
  };

  const handleBulkEditComplete = () => {
    setShowBulkEditModal(false);
    setBulkSelectMode(false);
    setSelectedPlayerIds([]);
    fetchPlayers();
  };

  const getSelectedPlayers = () => {
    return players?.filter(p => selectedPlayerIds?.includes(p?.id));
  };

  const handleCloseModal = async () => {
    setIsModalOpen(false);
    setSelectedPlayer(null);
    // Refresh player list to get latest data
    await fetchPlayers();
  };

  return (
    <>
      <Helmet>
        <title>{t('Player Profiles - ProX11 Market')}</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Header />

        <main className="pt-20 pb-24">
          <div className="bg-primary text-primary-foreground py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {t('Player Profiles')}
                  </h1>
                  <p className="text-gray-600">
                    {t('Discover and connect with talented African football players')}
                  </p>
                </div>
                {isAdmin && (
                  <div className="flex items-center space-x-3 mt-4 md:mt-0">
                    {bulkSelectMode ? (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setBulkSelectMode(false);
                            setSelectedPlayerIds([]);
                          }}
                        >
                          <Icon name="X" size={16} className="mr-2" />
                          Cancel Selection
                        </Button>
                        <Button
                          onClick={handleBulkEditClick}
                          disabled={selectedPlayerIds?.length === 0}
                          className="bg-blue-500 hover:bg-blue-600 text-white"
                        >
                          <Icon name="Edit" size={16} className="mr-2" />
                          Edit Selected ({selectedPlayerIds?.length})
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => setBulkSelectMode(true)}
                        variant="outline"
                      >
                        <Icon name="CheckSquare" size={16} className="mr-2" />
                        Bulk Edit Mode
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Bulk Selection Bar */}
              {bulkSelectMode && isAdmin && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <Checkbox
                        checked={selectedPlayerIds?.length === sortedPlayers?.length && sortedPlayers?.length > 0}
                        indeterminate={selectedPlayerIds?.length > 0 && selectedPlayerIds?.length < sortedPlayers?.length}
                        onChange={toggleSelectAll}
                        label="Select All"
                      />
                      <span className="text-sm text-blue-900 font-medium">
                        {selectedPlayerIds?.length} of {sortedPlayers?.length} player(s) selected
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Info" size={16} className="text-blue-600" />
                      <span className="text-sm text-blue-700">
                        Click on player cards to select/deselect
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
                <div className="flex flex-col lg:flex-row gap-6">
                  <aside className="lg:w-80 flex-shrink-0">
                    <FilterPanel
                      filters={filters}
                      onFilterChange={setFilters}
                      onClearFilters={handleClearFilters}
                      isOpen={isFilterOpen}
                      onToggle={() => setIsFilterOpen(!isFilterOpen)} />

                  </aside>

                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Input
                            type="text"
                            placeholder="Search player name..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e?.target?.value)}
                            onKeyPress={(e) => {
                              if (e?.key === 'Enter') {
                                setFilters({ ...filters, search: searchQuery });
                              }
                            }}
                            className="flex-1 max-w-md"
                          />
                          <Button
                            onClick={() => setFilters({ ...filters, search: searchQuery })}
                            className="flex items-center gap-2"
                          >
                            <Icon name="Search" size={16} />
                            Search
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Showing verified talent profiles with performance analytics
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground whitespace-nowrap">Sort by:</span>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e?.target?.value)}
                          className="px-3 py-2 bg-card border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary">

                          <option value="rating">Highest Rating</option>
                          <option value="value">Market Value</option>
                          <option value="age">Youngest First</option>
                          <option value="goals">Most Goals</option>
                        </select>
                      </div>
                    </div>

                    {loading ? (
                      <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                      </div>
                    ) : sortedPlayers?.length === 0 ? (
                      <div className="text-center py-20">
                        <Icon name="Users" size={48} className="mx-auto text-gray-400 mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          {t('No players found')}
                        </h3>
                        <p className="text-gray-600">
                          {t('Try adjusting your filters to see more results')}
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {sortedPlayers?.map((player) => (
                          <div key={player?.id} className="relative">
                            {bulkSelectMode && isAdmin && (
                              <div className="absolute top-2 left-2 z-10">
                                <Checkbox
                                  checked={selectedPlayerIds?.includes(player?.id)}
                                  onChange={() => togglePlayerSelection(player?.id)}
                                  className="bg-white rounded shadow-md"
                                />
                              </div>
                            )}
                            <PlayerCard
                              player={player}
                              onClick={() => handlePlayerClick(player)}
                              onCompare={() => handleCompare(player)}
                              isInComparison={comparisonPlayers?.some(p => p?.id === player?.id)}
                              isSelected={selectedPlayerIds?.includes(player?.id)}
                              bulkSelectMode={bulkSelectMode}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {selectedPlayer && !bulkSelectMode && isAdmin && (
          <AdminPlayerProfileModal
            player={selectedPlayer}
            onClose={() => setSelectedPlayer(null)}
            isOpen={!!selectedPlayer}
            onPlayerUpdated={handlePlayerUpdated}
          />
        )}

        {selectedPlayer && !bulkSelectMode && !isAdmin && (
          <PlayerDetailModal
            player={selectedPlayer}
            onClose={() => setSelectedPlayer(null)}
            onRequestConsultation={handleRequestConsultation}
            isOpen={!!selectedPlayer}
          />
        )}

        {showBulkEditModal && (
          <BulkEditModal
            selectedPlayers={getSelectedPlayers()}
            onClose={() => setShowBulkEditModal(false)}
            onPlayersUpdated={handleBulkEditComplete}
          />
        )}

        {isComparisonOpen && (
          <ComparisonPanel
            players={comparisonPlayers}
            onClose={() => setIsComparisonOpen(false)}
            onRemovePlayer={handleRemoveFromComparison}
            onClearAll={handleClearComparison}
          />
        )}
      </div>
    </>
  );

};

export default PlayerProfiles;